import React from 'react';

type SlateNode = {
  text?: string;
  children?: unknown;
  [key: string]: unknown;
};

type TableCell = { value?: unknown; [key: string]: unknown };
type TableRow = { cells?: unknown; [key: string]: unknown };

interface TableBlockProps {
  data?: { table?: { rows?: unknown; [key: string]: unknown } };
  [key: string]: unknown;
}

const isNode = (node: unknown): node is SlateNode =>
  !!node && typeof node === 'object' && !Array.isArray(node);

const isText = (node: SlateNode) => typeof node.text === 'string';

const emptyText = (): SlateNode[] => [{ text: '' }];

const paragraph = (children: SlateNode[]): SlateNode => ({
  type: 'p',
  children,
});

/**
 * Filhos de um elemento: lista não vazia, só de objetos, e todo elemento com
 * filhos (também sãos). É o caminho que `Node.first` percorre atrás de um
 * `Text`.
 */
const hasSaneChildren = (nodes: unknown): boolean =>
  Array.isArray(nodes) &&
  nodes.length > 0 &&
  nodes.every(
    (node) => isNode(node) && (isText(node) || hasSaneChildren(node.children)),
  );

/**
 * A raiz do valor de uma célula, além disso, só pode ter blocos — nada de
 * texto solto. O editor conta com isso (ver o HOC abaixo).
 */
const isSaneCellValue = (value: unknown): boolean =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (node) => isNode(node) && !isText(node) && hasSaneChildren(node.children),
  );

const sanitizeChildren = (nodes: unknown): SlateNode[] => {
  const list = Array.isArray(nodes) ? nodes.filter(isNode) : [];
  if (list.length === 0) return emptyText();
  return list.map((node) =>
    isText(node)
      ? node
      : { ...node, children: sanitizeChildren(node.children) },
  );
};

const sanitizeCellValue = (value: unknown): SlateNode[] => {
  const list = Array.isArray(value) ? value.filter(isNode) : [];
  if (list.length === 0) return [paragraph(emptyText())];

  // Texto solto na raiz: a célula inteira era um parágrafo implícito no HTML
  // de origem. Vale envolver tudo de uma vez, e não nó a nó, para não separar
  // um link do texto que vem colado nele.
  if (list.some(isText)) return [paragraph(sanitizeChildren(list))];

  return list.map((node) => ({
    ...node,
    children: sanitizeChildren(node.children),
  }));
};

/**
 * Devolve o `data` do bloco com as células normalizadas. Mantém a identidade
 * do objeto quando não há nada a consertar, para não forçar re-render nem
 * sujar o bloco à toa.
 */
export const sanitizeTableData = <P extends TableBlockProps>(
  data: P['data'],
): P['data'] => {
  const table = data?.table;
  const rows = table?.rows;
  if (!Array.isArray(rows)) return data;

  let changed = false;

  const nextRows = rows.map((row: TableRow) => {
    const cells = row?.cells;
    if (!Array.isArray(cells)) return row;

    let rowChanged = false;
    const nextCells = cells.map((cell: TableCell) => {
      if (isSaneCellValue(cell?.value)) return cell;
      rowChanged = true;
      changed = true;
      return { ...cell, value: sanitizeCellValue(cell?.value) };
    });

    return rowChanged ? { ...row, cells: nextCells } : row;
  });

  return changed ? { ...data, table: { ...table, rows: nextRows } } : data;
};

/**
 * Protege a edição do bloco `slateTable` contra células que o editor do
 * volto-slate não sabe abrir.
 *
 * O `deconstruct.js` do volto-slate monta cada célula com `z.children` cru do
 * HTML colado, sem envolver nada num bloco. Isso produz duas formas que
 * derrubam o formulário inteiro (o erro estoura no render/`componentDidUpdate`
 * do `SlateEditor`, fora de qualquer try/catch, e sobe até o ErrorBoundary):
 *
 * - `value: []`, de um `<td>` vazio. Ao selecionar o bloco, o `Cell` foca a
 *   célula (0,0) e o editor pede `Editor.start(editor, [])` num editor sem
 *   filhos: "Cannot get the start point in the node at path []".
 * - `value: [{ text: '...' }]`, texto solto na raiz, que é como está quase
 *   toda célula do conteúdo migrado. O `SlateEditor.render` lê
 *   `this.props.value[0].children.length` assim que o editor ganha seleção:
 *   "can't access property length, this.props.value[0].children is undefined".
 *
 * Daí a normalização ser a mesma que o Slate faria sozinho na primeira
 * alteração da célula (e que o `emptyCell` do core já usa ao criar tabela):
 * na raiz, só blocos; todo elemento com pelo menos um filho de texto. O
 * `type: 'p'` é o `defaultBlockType` do volto-slate, e o elemento sem `type`
 * que o Slate criaria renderiza pelo mesmo `<p>` do `elements.default`.
 *
 * Por que um HOC sobre a `edit` do registro, e não uma cópia (shadow) do
 * `SlateEditor.jsx`: `@plone/volto-slate` é um coreAddon, não um add-on
 * registrado em `volto.config.js`, e o caminho de customização dele é frágil.
 * Aqui só ajustamos as props que entram no bloco do core, sem congelar
 * componente nenhum dele.
 *
 * O saneamento é só de leitura: o dado gravado continua como está até que o
 * editor mexa na tabela — aí o `TableBlockEdit` grava a partir do `data` já
 * saneado que recebeu.
 */
export const withSafeTableCells = <P extends TableBlockProps>(
  Component: React.ComponentType<P>,
): React.FC<P> => {
  const SafeTableCells: React.FC<P> = (props) => {
    const { data } = props;
    const safeData = React.useMemo(() => sanitizeTableData<P>(data), [data]);
    return <Component {...props} data={safeData} />;
  };

  SafeTableCells.displayName = `withSafeTableCells(${
    Component?.displayName || Component?.name || 'Block'
  })`;

  return SafeTableCells;
};

export default withSafeTableCells;
